/**
 * Home-screen app catalog.
 *
 * Add apps here as we migrate Zep sideapps, self-hosted tools, or external links.
 * Local/migrated apps can live under /apps/<id>/ later.
 *
 * Fields:
 *   id          - stable slug used in /app/:id
 *   name        - display name (Arabic)
 *   description - short Arabic subtitle under the name
 *   icon        - emoji or image path (/img/...)
 *   url         - target URL (absolute or site-relative)
 *   embeddable  - if true, open inside iframe; if false, show "Open" button
 *   oidc        - if true, open via appLaunch OIDC handoff (not raw url)
 *   section     - section id from SECTIONS
 *   banner      - optional home-tile ribbon. String kind, or { kind, color }.
 *                 kind:  new | update | maintenance  (جديد | تحديث | صيانة)
 *                 color: red | green | yellow        (optional)
 *                 defaults: new=red, update=green, maintenance=yellow
 *                 e.g. banner: "new"
 *                      banner: { kind: "maintenance", color: "red" }
 */

import { appCanonicalOrigin } from "../../functions/utils/hosts.js";

export const BANNER_KINDS = {
  new: { label: "جديد", labelEn: "New", color: "red" },
  update: { label: "تحديث", labelEn: "Update", color: "green" },
  maintenance: { label: "صيانة", labelEn: "Maintenance", color: "yellow" },
};

const BANNER_KIND_ALIASES = {
  new: "new",
  جديد: "new",
  update: "update",
  تحديث: "update",
  maintenance: "maintenance",
  صيانة: "maintenance",
};

const BANNER_COLORS = new Set(["red", "green", "yellow"]);

const BANNER_COLOR_ALIASES = {
  red: "red",
  green: "green",
  yellow: "yellow",
  أحمر: "red",
  أخضر: "green",
  أصفر: "yellow",
};

function normalizeBannerKey(value) {
  return String(value || "").trim().toLowerCase();
}

/** Resolve a catalog `banner` field to { kind, color, label, labelEn }, or null. */
export function getAppBanner(app) {
  const raw = app && app.banner;
  if (!raw) return null;

  const kindRaw = typeof raw === "string" ? raw : raw.kind;
  const kind = BANNER_KIND_ALIASES[normalizeBannerKey(kindRaw)];
  if (!kind) return null;

  const spec = BANNER_KINDS[kind];
  const colorRaw = typeof raw === "object" && raw && raw.color ? raw.color : spec.color;
  const color = BANNER_COLOR_ALIASES[normalizeBannerKey(colorRaw)];
  return {
    kind,
    color: BANNER_COLORS.has(color) ? color : spec.color,
    label: spec.label,
    labelEn: spec.labelEn,
  };
}

export const SECTIONS = [
  { id: "social", title: "Social", titleAr: "اجتماعي" },
  { id: "entertainment", title: "Entertainment", titleAr: "ترفيه" },
  { id: "productivity", title: "Productivity", titleAr: "إنتاجية" },
];

/** @type {Record<string, {
 *   id: string,
 *   name: string,
 *   description?: string,
 *   icon: string,
 *   url: string,
 *   embeddable: boolean,
 *   oidc?: boolean,
 *   section: string,
 *   banner?: string | { kind: string, color?: string },
 * }>} */
export const APPS = {
  members: {
    id: "members",
    name: "الأعضاء",
    description: "قائمة أعضاء المساحة وإدارة الملف",
    icon: "👥",
    url: "/apps/members/",
    embeddable: true,
    section: "social",
  },
  activities: {
    id: "activities",
    name: "النشاطات",
    description: "جدولة النشاطات والدعوات",
    icon: "📅",
    url: "/apps/activities/",
    embeddable: true,
    section: "social",
  },
  colonist: {
    id: "colonist",
    name: "كاتان",
    description: "لعبة المستوطنين الجماعية",
    icon: "🏝️",
    url: "https://colonist.io",
    embeddable: true,
    section: "entertainment",
  },
  nodecast: {
    id: "nodecast",
    name: "التلفزيون",
    description: "مشاهدة جماعية للقنوات والعروض",
    icon: "📺",
    url: appCanonicalOrigin("nodecast"),
    embeddable: true,
    oidc: true,
    section: "entertainment",
    banner: "update",
  },
  mealie: {
    id: "mealie",
    name: "الوصفات",
    description: "مكتبة وصفات المجتمع",
    icon: "🍳",
    url: appCanonicalOrigin("mealie"),
    embeddable: true,
    oidc: true,
    section: "social",
    banner: "maintenance",
  },
  watchparty: {
    id: "watchparty",
    name: "مشاهدة جماعية",
    description: "شاهد يوتيوب والفيديو معاً في غرفة واحدة",
    icon: "🍿",
    url: appCanonicalOrigin("watchparty"),
    embeddable: true,
    section: "social",
    banner: "new",
  },
  librechat: {
    id: "librechat",
    name: "الذكاء الاصطناعي",
    description: "محادثة ومساعدة بالذكاء الاصطناعي",
    icon: "🤖",
    url: appCanonicalOrigin("librechat"),
    embeddable: true,
    oidc: true,
    section: "productivity",
    banner: "new",
  },
};

export function getApp(appId) {
  if (!appId) return null;
  return APPS[appId] || null;
}

/** Sections that currently have at least one app (empty sections are omitted). */
export function getSectionsWithApps() {
  return SECTIONS.map((section) => {
    const apps = Object.values(APPS).filter((app) => app.section === section.id);
    return { ...section, apps };
  }).filter((section) => section.apps.length > 0);
}
